import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UserProfileDescription from "./UserProfileDescription";
import CustomTabulation from "../../../custom-components/CustomTabulation";
import { UserProfileTabsEnum } from "../../../domain/enums/UserProfileTabsEnum";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch } from "../../../custom-hooks/useAppDispatch";
import { DEFAULT_OFFSET, DEFAULT_PAGE, RECORDS_PER_PAGE } from "../../../domain/constants";
import { WorkServiceImpl } from "../../../services/WorkServiceImpl";
import { SortingOrder } from "../../../domain/enums/common/SortingOrderEnum";
import { useSelector } from "react-redux";
import { selectWorks } from "../../../slices/works-slice";
import WorkList from "../../resources/components/work-components/work-list/WorkList";
import { selectReviews } from "../../../slices/reviews-slice";
import ReviewList from "../../resources/components/review-components/ReviewList";
import { ReviewServiceImpl } from "../../../services/ReviewServiceImpl";
import { useErrorHandler } from "../../../custom-hooks/base-hooks/useErrorHandler";
import { StatusCodes } from "http-status-codes";
import { WorkListType } from "../../../domain/models/Work";
import { getUserCoords, selectUser } from "../../../slices/user-slice";

const UserProfilePage: FC = () => {
    const [tabName, setTabName] = useState(UserProfileTabsEnum.REVIEWS);
    const [page, setPage] = useState(0);
    const { t } = useTranslation();
    const navigate = useNavigate();

    const dispatch = useAppDispatch();

    const works = useSelector(selectWorks);
    const reviews = useSelector(selectReviews);
    const user = useSelector(selectUser());

    const { clientId } = useParams();

    const tabSwitchHandler = (tab: UserProfileTabsEnum) => {
        setTabName(tab);
    };

    const isReviews = tabName === UserProfileTabsEnum.REVIEWS;
    const isCompletedWorks = tabName === UserProfileTabsEnum.COMPLETED_WORKS;

    const uploadWorks = async (resetPage: boolean) => {
        const currentPage = resetPage ? DEFAULT_PAGE : page + 1;
        await dispatch(
            WorkServiceImpl.getWorksThunk({
                page: currentPage,
                limit: RECORDS_PER_PAGE,
                form: {
                    [isCompletedWorks ? "showOnlyInactive" : "showInactive"]: true,
                    price: SortingOrder.ASC,
                    distance: SortingOrder.ASC,
                    startFrom: SortingOrder.DESC,
                },
                getCurrentUserWorks: true,
                clientId,
                coords: await getUserCoords(user),
            })
        );
        setPage(currentPage);
    };

    const uploadReviews = async (resetPage: boolean) => {
        const currentPage = resetPage ? DEFAULT_PAGE : page + 1;
        await dispatch(
            ReviewServiceImpl.getUserReviewsThunk({
                page: currentPage,
                limit: RECORDS_PER_PAGE,
                offset: DEFAULT_OFFSET,
                userId: clientId,
            })
        );
        setPage(currentPage);
    };

    const uploadWorksCallback = useCallback(async () => {
        uploadWorks(false);
    }, [page, tabName, clientId]);

    const uploadReviewsCallback = useCallback(async () => {
        uploadReviews(false);
    }, [page, tabName, clientId]);

    useEffect(() => {
        uploadWorks(true);
        uploadReviews(true);
    }, [tabName, clientId]);

    const { isError: notFound, resetErrors: resetNotFoundErrors } = useErrorHandler(StatusCodes.NOT_FOUND);
    useEffect(() => {
        return () => {
            resetNotFoundErrors();
        };
    }, []);

    if (notFound()) {
        navigate("/not-found", { replace: true });
        return;
    }

    return (
        <div className="relative dmin2:mx-auto di6:mx-[26px] ti6:mx-8 ti7:mx-auto tmax4:mx-[15px] flex flex-col dmin2:w-[1280px] ti9:w-[92.5%] mb-[80px] dmax2:mb-[60px]">
            {!clientId && <h1 className="H32 text-textWhite mb-9 dmin2:mt-5">{t("common.myProfile")}</h1>}
            <UserProfileDescription worksMeta={works.meta} />
            <CustomTabulation
                tabsEnum={UserProfileTabsEnum}
                tabName={tabName}
                tabSwitchHandler={tabSwitchHandler}
                tabTitleKey="userProfile"
                tabTitleInterpolationObject={{
                    reviewsCount: reviews.totalRows,
                    completedWorksCount: works.meta.completedWorksCount,
                    createdWorksCount: works.meta.createdWorksCount,
                }}
                wrapClassName="mb-9 overflow-x-auto no-scrollbar"
                unorderedListClassName="min-w-[610px] gap-8 dmax2:gap-3"
                listItemClassName="flex items-center"
                tabNameActiveClassName="H32 dmax2:H20B text-textWhite pb-[6px] dmax2:pb-1 border-b-4 dmax2:border-b-3 border-textWarning"
                tabNameClassName="H24B dmax2:H16B text-textFaded pb-[6px] dmax2:pb-[10px] border-textWarning"
            />
            {isReviews ? (
                <ReviewList uploadReviewsCallback={uploadReviewsCallback} />
            ) : (
                <WorkList
                    type={WorkListType.COMMON}
                    uploadWorksCallback={uploadWorksCallback}
                    customEmptyEntitiesContent={
                        <div className="flex flex-col items-center">
                            <p className="H24 dmax2:H20 text-textFaded">{t("userProfile.emptyWorks")}</p>
                            {!clientId && (
                                <Link
                                    to="/create-work"
                                    className="dmin2:H16 td2:H14 tmax1:H12 text-textWhite bg-primaryBlue hover:bg-primaryBlueFocused dmin2:w-[220px] td2:w-[198px] tmax1:w-[187px] dmin2:h-[50px] td2:h-[40px] tmax1:h-[37px] mt-9 dmax2:mt-6 rounded-xl dmax2:rounded-lg flex justify-center items-center px-6 cursor-pointer transition-all duration-300"
                                >
                                    <div className="mb-[2px]">{t("userProfile.firstWorkButton")}</div>
                                </Link>
                            )}
                        </div>
                    }
                />
            )}
        </div>
    );
};

{
    /* <div className="relative flex flex-col bg-backgroundInput rounded-[10px] py-6 px-8 max-w-[845px]">
                    <div className="flex tmax4:flex-col justify-between w-full mb-5 tmax4:mb-4">
                        <p className="dmin2:H24B td4:H20B tmax4:H16B text-textWhite tmax4:mb-4">
                            Перемістити продукцію на склад
                        </p>
                        <div className="dmin2:H16 td2:H14 tmax1:H12 text-textWhite bg-primaryBlue dmin2:h-[40px] td2:h-[35px] tmax1:h-[30px] rounded-[30px] tmax4:w-[126px] flex justify-center items-center px-4">
                            <CurrencyIconSVG
                                className="w-6 tmax4:w-4 mr-[10px] tmax4:mr-[6px]"
                                stroke="white"
                                strokeWidth="2"
                            />
                            <div className="mb-[2px]">250 грн/год</div>
                        </div>
                    </div>
                    <div className="flex flex-col mb-5">
                        <CustomFiveStarsRating wrapClassName="mb-3 tmax4:mb-2" />
                        <p className="H16 tmax4:H14 text-textWhite">
                            Якісна робота, приємний у спілкуванні, завжди на зв’язку. Всі проблеми вирішувалися швидко
                        </p>
                    </div>
                    <div className="relative flex justify-between w-full">
                        <div className="relative flex items-center w-full">
                            <img
                                src="images/no-photo.png"
                                alt="user"
                                className="w-[62px] tmax4:w-[46px] h-[62px] tmax4:h-[46px] rounded-full mr-5"
                            />
                            <div className="relative flex flex-col w-full">
                                <p className="H16B tmax4:H14B text-textWhite mb-2">Антон Коломиєць</p>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <span className="H16B tmax4:H14B text-textWhite mr-1 dmax2:mr-[2px]">5,0</span>
                                        <RatingStarSVG className="w-[21px] dmax2:w-5 mr-2 dmax2:mr-1" />
                                        <span className="H16B tmax4:H14B text-textFaded">(12)</span>
                                    </div>
                                    <div className="H16 tmax4:H14 text-textFaded">29 вересня 14:18</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> */
}

export default UserProfilePage;
